<template>
  <modal @cancel="goRates" :large="true">
    <h3 class="t-text-black t-font-bold" slot="title">
      {{ modalTitle }}
    </h3>

    <loading-alert
      v-if="hasError"
      :text="errorMsg"
      :icon="'error'"
      :color="'danger'"
    ></loading-alert>
    <loading-alert v-if="loading"></loading-alert>

    <form
      v-if="!loading && !hasError"
      @submit.prevent="submitForm()"
      class="t-bg-white t-rounded t-px-8 t-pt-6 t-pb-8"
    >
      <div class="md:t-flex md:t-items-start t-mb-6">
        <div class="md:t-w-1/3"></div>
        <div class="md:t-w-2/3">
          <label class="t-block t-text-black t-font-bold md:t-mb-0 t-pr-4">
            Required Settings
          </label>
          <hr />
        </div>
      </div>

      <div class="md:t-flex md:t-items-start t-mb-6">
        <div class="md:t-w-1/3">
          <label
            class="t-block t-text-gray-500 t-font-bold md:t-text-right md:t-mb-0 t-pr-4"
            >Pickup Area (*)</label
          >
        </div>
        <div class="md:t-w-2/3">
          <select
            v-model="form.pickupArea"
            v-validate="'required'"
            required
            class="t-block t-appearance-none t-w-2/3 t-bg-gray-200 t-border t-border-gray-200 t-text-gray-700 t-py-3 t-px-4 t-pr-8 t-rounded t-leading-tight focus:t-outline-none focus:t-bg-white focus:t-border-gray-500"
          >
            :class="{ 'is-invalid': errors.has('pickupArea') }"
            name="pickupArea">
            <option
              v-for="area in customAreas"
              :value="area.id"
              :key="area.id"
              >{{ area.name }}</option
            >
          </select>

          <div
            v-show="errors.has('pickupArea')"
            class="t-text-red-500 t-text-sm"
          >
            Please select a valid pickup area.
          </div>

          <small class="t-text-sm t-text-gray-500">
            You can create new custom areas
            <a class="t-text-blue-500" href="/settings/custom-areas/create"
              >here</a
            >.
          </small>
        </div>
      </div>

      <div class="md:t-flex md:t-items-start t-mb-6">
        <div class="md:t-w-1/3">
          <label
            class="t-block t-text-gray-500 t-font-bold md:t-text-right md:t-mb-0 t-pr-4"
            >Delivery Area (*)</label
          >
        </div>

        <div class="md:t-w-2/3">
          <select
            v-model="form.deliveryArea"
            v-validate="'required'"
            required
            class="t-block t-appearance-none t-w-2/3 t-bg-gray-200 t-border t-border-gray-200 t-text-gray-700 t-py-3 t-px-4 t-pr-8 t-rounded t-leading-tight focus:t-outline-none focus:t-bg-white focus:t-border-gray-500"
            :class="{ 'is-invalid': errors.has('deliveryArea') }"
            name="deliveryArea"
          >
            <option
              v-for="area in customAreas"
              :value="area.id"
              :key="area.id"
              >{{ area.name }}</option
            >
          </select>

          <div
            v-show="errors.has('deliveryArea')"
            class="t-text-red-500 t-text-sm"
          >
            Please select a valid delivery area.
          </div>
        </div>
      </div>

      <div class="md:t-flex md:t-items-start t-mb-6">
        <div class="md:t-w-1/3">
          <label
            class="t-block t-text-gray-500 t-font-bold md:t-text-right md:t-mb-0 t-pr-4"
            >Types of goods (*)</label
          >
        </div>

        <div class="md:t-w-2/3">
          <multiselect
            v-model="form.goodsTypes"
            :close-on-select="false"
            :hide-selected="true"
            track-by="id"
            :options="goodTypes"
            :multiple="true"
            label="name"
          >
          </multiselect>

          <div
            v-show="errors.has('goodsTypes')"
            :class="{ 't-block': errors.has('goodsTypes') }"
            class="t-text-red-500 t-text-sm"
          >
            Please select at least one type of goods
          </div>

          <small class="t-text-sm t-text-gray-500">
            The types of goods this route can carry.
          </small>
        </div>
      </div>

      <div class="md:t-flex md:t-items-start t-mb-6">
        <div class="md:t-w-1/3">
          <label
            class="t-block t-text-gray-500 t-font-bold md:t-text-right md:t-mb-0 t-pr-4"
            >Types of packages (*)</label
          >
        </div>

        <div class="md:t-w-2/3">
          <multiselect
            v-model="form.packageTypes"
            :close-on-select="false"
            :hide-selected="true"
            track-by="id"
            :options="packageTypes"
            :multiple="true"
            label="name"
          >
          </multiselect>

          <div
            v-show="errors.has('packageTypes')"
            :class="{ 't-block': errors.has('packageTypes') }"
            class="t-text-red-500 t-text-sm"
          >
            Please select at least one type of goods
          </div>

          <small class="t-text-sm t-text-gray-500">
            The types of packages this route can carry
          </small>
        </div>
      </div>

      <div class="md:t-flex md:t-items-start t-mb-6">
        <div class="md:t-w-1/3">
          <label
            for="rate"
            class="t-block t-text-gray-500 t-font-bold md:t-text-right md:t-mb-0 t-pr-4"
            >Rate (*)</label
          >
        </div>

        <div class="md:t-w-2/3">
          <div class="t-flex">
            <div class="md:t-w-1/2">
              <span
                class="t-appearance-none t-bg-gray-200 t-w-1/6 t-border t-border-gray-200 t-text-gray-700 t-py-3 t-px-3 t-rounded t-leading-tight focus:t-outline-none focus:t-bg-white focus:t-border-gray-500"
                >$</span
              >
              <input
                v-model="form.rate"
                v-validate.disable="'required|decimal'"
                type="text"
                name="rate"
                :class="{ 'is-invalid': errors.has('rate') }"
                id="rate"
                placeholder="50.00"
                required
                class="t-appearance-none t-bg-gray-200 t-w-5/6 t-border t-border-gray-200 t-text-gray-700 t-py-3 t-px-4 t-pr-8 t-rounded t-leading-tight focus:t-outline-none focus:t-bg-white focus:t-border-gray-500"
              />

              <div v-show="errors.has('rate')" class="t-text-red-500 t-text-sm">
                Please enter a numeric rate
              </div>
            </div>

            <div class="md:t-w-1/2">
              <select
                v-model="form.rateType"
                v-validate="'required'"
                name="rateType"
                :class="{ 'is-invalid': errors.has('rateType') }"
                required
                class="t-block t-appearance-none t-bg-gray-200 t-border t-border-gray-200 t-text-gray-700 t-py-3 t-px-4 t-pr-8 t-rounded t-leading-tight focus:t-outline-none focus:t-bg-white focus:t-border-gray-500"
              >
                <option value="PER_TONNE">per tonne</option>
                <option value="PER_TONNE_KM">per tonne Km</option>
                <option value="PER_PACKAGE">per package</option>
                <option value="PER_M3">per m3</option>
              </select>

              <div
                v-show="errors.has('rateType')"
                class="t-text-red-500 t-text-sm"
              >
                Please select a type of rate
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        id="volume-to-weight-holder"
        class="md:t-flex md:t-items-start t-mb-6"
      >
        <div class="md:t-w-1/3">
          <label
            class="t-block t-text-gray-500 t-font-bold md:t-text-right md:t-mb-0 t-pr-4"
            >Volume to Weight Ratio (*)</label
          >
        </div>

        <div class="md:t-w-2/3">
          <select
            v-model="form.volumeWeightRatio"
            v-validate="'required'"
            :class="{ 'is-invalid': errors.has('volumeWeightRatio') }"
            name="volumeWeightRatio"
            required
            class="t-block t-appearance-none t-w-2/3 t-bg-gray-200 t-border t-border-gray-200 t-text-gray-700 t-py-3 t-px-4 t-pr-8 t-rounded t-leading-tight focus:t-outline-none focus:t-bg-white focus:t-border-gray-500"
          >
            <option value="1">1</option>
            <option value="2">2</option>
            <option value="3">3</option>
          </select>

          <div
            v-show="errors.has('volumeWeightRatio')"
            class="t-text-red-500 t-text-sm"
          >
            Please select a volume to weight ratio
          </div>
        </div>
      </div>

      <div class="md:t-flex md:t-items-start t-mb-6">
        <div class="md:t-w-1/3">
          <label
            for="min_charge"
            class="t-block t-text-gray-500 t-font-bold md:t-text-right md:t-mb-0 t-pr-4"
            >Minimum Charge (*)</label
          >
        </div>

        <div class="md:t-w-2/3">
          <div class="t-flex">
            <span
              class="t-appearance-none t-bg-gray-200 t-border t-border-gray-200 t-text-gray-700 t-py-3 t-px-3 t-rounded t-leading-tight focus:t-outline-none focus:t-bg-white focus:t-border-gray-500"
              >$</span
            >
            <input
              v-model="form.minCharge"
              v-validate.disable="'required|decimal'"
              :class="{ 'is-invalid': errors.has('minCharge') }"
              type="text"
              name="minCharge"
              id="min_charge"
              placeholder="75.00"
              required
              class="t-block t-appearance-none t-w-2/3 t-bg-gray-200 t-border t-border-gray-200 t-text-gray-700 t-py-3 t-px-4 t-pr-8 t-rounded t-leading-tight focus:t-outline-none focus:t-bg-white focus:t-border-gray-500"
            />
          </div>
          <div
            v-show="errors.has('minCharge')"
            class="t-text-red-500 t-text-sm"
          >
            Please enter a numeric minimum charge
          </div>
        </div>
      </div>

      <div class="md:t-flex md:t-items-start t-mb-6">
        <div class="md:t-w-1/3">
          <label
            class="t-block t-text-gray-500 t-font-bold md:t-text-right md:t-mb-0 t-pr-4"
          >
            Flag Fall (*)
            <div
              class="address-service t-inline t-text-gray-500"
              v-tooltip:bottom="
                'A fixed fee added to every booking (unless a booking is the minimum charge)'
              "
            >
              <i class="far fa-info-circle"></i>
            </div>
          </label>
        </div>

        <div class="md:t-w-2/3">
          <div class="t-flex">
            <span
              class="t-appearance-none t-bg-gray-200 t-border t-border-gray-200 t-text-gray-700 t-py-3 t-px-3 t-rounded t-leading-tight focus:t-outline-none focus:t-bg-white focus:t-border-gray-500"
              >$</span
            >
            <input
              v-model="form.flagFall"
              v-validate.disable="'required|decimal'"
              :class="{ 'is-invalid': errors.has('flagFall') }"
              name="flagFall"
              type="text"
              placeholder="10.00"
              required
              class="t-block t-appearance-none t-w-2/3 t-bg-gray-200 t-border t-border-gray-200 t-text-gray-700 t-py-3 t-px-4 t-pr-8 t-rounded t-leading-tight focus:t-outline-none focus:t-bg-white focus:t-border-gray-500"
            />
          </div>
          <div v-show="errors.has('flagFall')" class="t-text-red-500 t-text-sm">
            Please enter a numeric flag fall
          </div>
        </div>
      </div>

      <div class="md:t-flex md:t-items-start t-mb-6">
        <div class="md:t-w-1/3">
          <label
            class="t-block t-text-gray-500 t-font-bold md:t-text-right md:t-mb-0 t-pr-4"
            >Maximum Daily Capacity (*)</label
          >
        </div>
        <div class="md:t-w-2/3">
          <div class="t-flex">
            <input
              v-model="form.maxDailyCapacity"
              v-validate.disable="'required|decimal'"
              :class="{ 'is-invalid': errors.has('maxDailyCapacity') }"
              name="maxDailyCapacity"
              type="text"
              placeholder="5000"
              required
              class="t-block t-appearance-none t-w-2/3 t-bg-gray-200 t-border t-border-gray-200 t-text-gray-700 t-py-3 t-px-4 t-pr-8 t-rounded t-leading-tight focus:t-outline-none focus:t-bg-white focus:t-border-gray-500"
            />
            <span
              class="t-appearance-none t-bg-gray-200 t-border t-border-gray-200 t-text-gray-700 t-py-3 t-px-3 t-rounded t-leading-tight focus:t-outline-none focus:t-bg-white focus:t-border-gray-500"
              >Kilograms</span
            >
          </div>

          <div
            v-show="errors.has('maxDailyCapacity')"
            class="t-text-red-500 t-text-sm"
          >
            Please enter a numeric max daily capacity
          </div>
        </div>
      </div>

      <div class="md:t-flex md:t-items-start t-mb-6">
        <div class="md:t-w-1/3">
          <label
            for="cutoff_time"
            class="t-block t-text-gray-500 t-font-bold md:t-text-right md:t-mb-0 t-pr-4"
            >Booking Cutoff Time (*)</label
          >
        </div>

        <div class="md:t-w-2/3 t-flex">
          <div class="md:t-w-1/2">
            <select
              v-model="form.cutoffTime"
              v-validate="'required'"
              :class="{ 'is-invalid': errors.has('cutoffTime') }"
              name="cutoffTime"
              id="cutoff_time"
              class="t-block t-appearance-none t-w-full t-bg-gray-200 t-border t-border-gray-200 t-text-gray-700 t-py-3 t-px-4 t-pr-8 t-rounded t-leading-tight focus:t-outline-none focus:t-bg-white focus:t-border-gray-500"
              required
            >
              <option value="05:00">5:00 am</option>
              <option value="05:30">5:30 am</option>
              <option value="06:00">6:00 am</option>
              <option value="06:30">6:30 am</option>
              <option value="07:00">7:00 am</option>
              <option value="07:30">7:30 am</option>
              <option value="08:00">8:00 am</option>
              <option value="08:30">8:30 am</option>
              <option value="09:00">9:00 am</option>
              <option value="09:30">9:30 am</option>
              <option value="10:00">10:00 am</option>
              <option value="10:30">10:30 am</option>
              <option value="11:00">11:00 am</option>
              <option value="11:30">11:30 am</option>
              <option value="12:00">12:00 pm</option>
              <option value="12:30">12:30 pm</option>
              <option value="13:00">1:00 pm</option>
              <option value="13:30">1:30 pm</option>
              <option value="14:00">2:00 pm</option>
              <option value="14:30">2:30 pm</option>
              <option value="15:00">3:00 pm</option>
              <option value="15:30">3:30 pm</option>
              <option value="16:00">4:00 pm</option>
              <option value="16:30">4:30 pm</option>
              <option value="17:00">5:00 pm</option>
              <option value="17:30">5:30 pm</option>
              <option value="18:00">6:00 pm</option>
              <option value="18:30">6:30 pm</option>
              <option value="19:00">7:00 pm</option>
              <option value="19:30">7:30 pm</option>
            </select>

            <div
              v-show="errors.has('cutoffTime')"
              class="t-text-red-500 t-text-sm"
            >
              Please select a cutoff time
            </div>
          </div>

          <div class="md:t-w-1/2">
            <div class="md:t-w-1/2">
              <select
                v-model="form.cutoffDay"
                v-validate="'required'"
                :class="{ 'is-invalid': errors.has('cutoffDay') }"
                name="cutoffDay"
                class="t-block t-appearance-none t-bg-gray-200 t-border t-border-gray-200 t-text-gray-700 t-ml-2 t-py-3 t-px-4 t-pr-8 t-rounded t-leading-tight focus:t-outline-none focus:t-bg-white focus:t-border-gray-500"
                required
              >
                <option value="0">For same day pickup</option>
                <option value="1">For next day pickup</option>
              </select>

              <div
                v-show="errors.has('cuttoffDay')"
                class="t-text-red-500 t-text-sm"
              >
                Please select a cutoff time
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="md:t-flex md:t-items-start t-mb-6">
        <div class="md:t-w-1/3">
          <label
            for="service_level"
            class="t-block t-text-gray-500 t-font-bold md:t-text-right md:t-mb-0 t-pr-4"
            >Service Level (*)</label
          >
        </div>
        <div class="md:t-w-2/3">
          <select
            id="service_level"
            v-validate="'required'"
            v-model="form.serviceLevel"
            :class="{ 'is-invalid': errors.has('serviceLevel') }"
            name="serviceLevel"
            required
            class="t-block t-appearance-none t-bg-gray-200 t-border t-border-gray-200 t-text-gray-700 t-py-3 t-px-4 t-pr-8 t-rounded t-leading-tight focus:t-outline-none focus:t-bg-white focus:t-border-gray-500"
          >
            <option value="-1">Delivered by 1PM same day as pickup</option>
            <option value="0">Delivered same day as pickup</option>
            <option value="1">Delivered next working day after pickup</option>
            <option value="2">Delivered 2 working days after pickup</option>
            <option value="3">Delivered 3 working days after pickup</option>
            <option value="4">Delivered 4 working days after pickup</option>
            <option value="5">Delivered 5 working days after pickup</option>
            <option value="6">Delivered 6 working days after pickup</option>
            <option value="7">Delivered 7 working days after pickup</option>
          </select>

          <div
            v-show="errors.has('serviceLevel')"
            class="t-text-red-500 t-text-sm"
          >
            Please select a service level
          </div>
        </div>
      </div>

      <div class="md:t-flex md:t-items-start t-mb-6">
        <div class="md:t-w-1/3">
          <label
            for="pickup_days"
            class="t-block t-text-gray-500 t-font-bold md:t-text-right md:t-mb-0 t-pr-4"
          >
            Can Pickup On (*)
          </label>
        </div>
        <div class="md:t-w-2/3">
          <multiselect
            v-model="form.pickupDays"
            id="pickup_days"
            :close-on-select="false"
            :hide-selected="true"
            track-by="id"
            :options="days"
            :multiple="true"
            label="name"
            :class="{ 'is-invalid': errors.has('pickupDays') }"
          >
          </multiselect>

          <div
            v-show="errors.has('pickupDays')"
            :class="{ 't-block': errors.has('pickupDays') }"
            class="t-text-red-500 t-text-sm"
          >
            Please select at least one pickup day
          </div>
        </div>
      </div>

      <div class="md:t-flex md:t-items-start t-mb-6">
        <div class="md:t-w-1/3">
          <label
            for="delivery_days"
            class="t-block t-text-gray-500 t-font-bold md:t-text-right md:t-mb-0 t-pr-4"
          >
            Can Deliver On (*)
          </label>
        </div>
        <div class="md:t-w-2/3">
          <div :class="{ 'is-valid': errors.has('deliveryDays') }">
            <multiselect
              v-model="form.deliveryDays"
              id="delivery_days"
              :close-on-select="false"
              :hide-selected="true"
              track-by="id"
              :options="days"
              :multiple="true"
              label="name"
              :class="{ 'is-invalid': errors.has('deliveryDays') }"
            >
            </multiselect>
          </div>

          <div
            v-show="errors.has('deliveryDays')"
            :class="{ 't-block': errors.has('deliveryDays') }"
            class="t-text-red-500 t-text-sm"
          >
            Please select at least one delivery day
          </div>
        </div>
      </div>

      <div class="md:t-flex md:t-items-start t-mb-6">
        <div class="md:t-w-1/3">
          <label
            for="live"
            class="t-block t-text-gray-500 t-font-bold md:t-text-right md:t-mb-0 t-pr-4"
          >
            Make This Offer Live To Customers Now (*)
          </label>
        </div>
        <div class="md:t-w-2/3">
          <select
            v-model="form.live"
            id="live"
            name="live"
            required
            class="t-block t-appearance-none t-w-2/3 t-bg-gray-200 t-border t-border-gray-200 t-text-gray-700 t-py-3 t-px-4 t-pr-8 t-rounded t-leading-tight focus:t-outline-none focus:t-bg-white focus:t-border-gray-500"
          >
            <option value="1">Yes</option>
            <option value="0">No</option>
          </select>
          <small class="t-text-gray-500 t-text-sm">
            You can add rates but make them available at a later time if you
            wish.
          </small>
        </div>
      </div>

      <div class="md:t-flex md:t-items-start t-mb-6">
        <div class="md:t-w-1/3">
          <label
            for="offer_expiry"
            class="t-block t-text-gray-500 t-font-bold md:t-text-right md:t-mb-0 t-pr-4"
            >Offer Expiry (*)</label
          >
        </div>
        <div id="expiry-date-holder" class="md:t-w-2/3">
          <datepicker
            class="t-block t-appearance-none t-w-2/3 t-bg-gray-200 t-border t-border-gray-200 t-text-gray-700 t-py-3 t-px-4 t-pr-8 t-rounded t-leading-tight focus:t-outline-none focus:t-bg-white focus:t-border-gray-500"
            v-model="form.expiry"
            id="offer_expiry"
            placeholder="Select Date"
          >
          </datepicker>
          <small class="t-text-gray-500 t-text-sm">
            This offer will be taken offline on this date. You can extend or
            shorten the expiry date at any time.
          </small>
        </div>
      </div>

      <div class="md:t-flex md:t-items-start t-my-6">
        <div class="md:t-w-1/3"></div>
        <div class="md:t-w-2/3">
          <label class="t-block t-text-black t-font-bold md:t-mb-0 t-pr-4">
            Optional Settings
          </label>
          <hr />
        </div>
      </div>

      <div class="md:t-flex md:t-items-start t-mb-6">
        <div class="md:t-w-1/3">
          <label
            for="start_restriction"
            class="t-block t-text-gray-500 t-font-bold md:t-text-right md:t-mb-0 t-pr-4"
          >
            Start of Month Restriction
          </label>
        </div>
        <div class="md:t-w-2/3">
          <input
            v-model="form.startMonthRestriction"
            name="start_restriction"
            type="text"
            placeholder="Enter the number of working days"
            class="t-block t-appearance-none t-w-2/3 t-bg-gray-200 t-border t-border-gray-200 t-text-gray-700 t-py-3 t-px-4 t-pr-8 t-rounded t-leading-tight focus:t-outline-none focus:t-bg-white focus:t-border-gray-500"
          />
          <small class="t-text-gray-500 t-text-sm">
            The number of working days from the start of each month this offer
            is not available.
          </small>
        </div>
      </div>

      <div class="md:t-flex md:t-items-start t-mb-6">
        <div class="md:t-w-1/3">
          <label
            for="end_restriction"
            class="t-block t-text-gray-500 t-font-bold md:t-text-right md:t-mb-0 t-pr-4"
          >
            End of Month Restriction
          </label>
        </div>
        <div class="md:t-w-2/3">
          <input
            v-model="form.endMonthRestriction"
            name="end_restriction"
            type="text"
            placeholder="Enter the number of working days"
            class="t-block t-appearance-none t-w-2/3 t-bg-gray-200 t-border t-border-gray-200 t-text-gray-700 t-py-3 t-px-4 t-pr-8 t-rounded t-leading-tight focus:t-outline-none focus:t-bg-white focus:t-border-gray-500"
          />
          <small class="t-text-gray-500 t-text-sm">
            The number of working days from the end of each month this offer is
            not available.
          </small>
        </div>
      </div>

      <div class="t-flex t-items-center t-justify-end t-pt-4">
        <button
          @click="goRates"
          type="button"
          class="t-bg-gray-500 hover:t-bg-gray-700 t-text-white t-font-bold t-py-2 t-px-4 t-mx-2 t-rounded focus:t-outline-none focus:t-shadow-outline"
        >
          Close
        </button>
        <button
          v-if="!saving && errors.count() > 0"
          v-tooltip:top="[
            '<strong class=\'t-text-center\'>Please fix the errors above</strong>',
            true
          ]"
          class="t-bg-gray-500 hover:t-bg-gray-700 t-text-white t-font-bold t-py-2 t-px-4 t-mx-2 t-rounded focus:t-outline-none focus:t-shadow-outline disabled"
          type="button"
        >
          Update
        </button>
        <button
          v-else
          :disabled="saving"
          type="submit"
          class="t-bg-green-500 hover:t-bg-green-700 t-text-white t-font-bold t-py-2 t-px-4 t-mx-2 t-rounded focus:t-outline-none focus:t-shadow-outline"
        >
          <i v-if="saving" class="fa fa-spinner fa-spin"></i>
          <span v-else>{{ submitButtonText }}</span>
        </button>
      </div>
    </form>
  </modal>
</template>

<script>
import Modal from "../../utils/Modal_v2";
import LoadingAlert from "../../utils/LoadingAlert";
import Datepicker from "vuejs-datepicker";

export default {
  name: "fixed-rates-form",
  components: {
    Modal,
    LoadingAlert,
    Datepicker
  },
  props: [],
  $_veeValidate: {
    validator: "new"
  },
  data() {
    return {
      saving: false,
      packageTypes: null,
      goodTypes: null,
      customAreas: null,
      form: {
        pickupArea: null,
        deliveryArea: null,
        goodsTypes: [],
        packageTypes: [],
        rate: null,
        rateType: "PER_TONNE",
        volumeWeightRatio: 3,
        minCharge: null,
        flagFall: null,
        maxDailyCapacity: null,
        cutoffTime: "10:00",
        cutoffDay: 0,
        serviceLevel: 0,
        pickupDays: [
          {
            id: 1,
            name: "Monday"
          },
          {
            id: 2,
            name: "Tuesday"
          },
          {
            id: 3,
            name: "Wednesday"
          },
          {
            id: 4,
            name: "Thursday"
          },
          {
            id: 5,
            name: "Friday"
          }
        ],
        deliveryDays: [
          {
            id: 1,
            name: "Monday"
          },
          {
            id: 2,
            name: "Tuesday"
          },
          {
            id: 3,
            name: "Wednesday"
          },
          {
            id: 4,
            name: "Thursday"
          },
          {
            id: 5,
            name: "Friday"
          }
        ],
        live: 1,
        expiry: moment()
          .add(3, "years")
          .toDate(),
        startMonthRestriction: 0,
        endMonthRestriction: 0
      },
      errorMsg: null,
      rate: null,
      isCreate: null
    };
  },

  mounted() {
    this.loadPackageTypes();
    this.loadGoodTypes();
    this.loadCustomAreas();
    if (this.$route.path.includes("new")) {
      this.isCreate = true;
    } else {
      this.isCreate = false;
      //fetch the offer when editing a rate offer
      this.fetchFixedRate();
    }
  },

  methods: {
    goRates() {
      this.$router.replace("/carry-freight/fixed-rates");
    },

    submitForm() {
      this.$validator.validateAll().then(res => {
        if (res) {
          let tooltip = document.querySelectorAll(".tooltip");
          for (var index = 0; index < tooltip.length; index++) {
            tooltip[index].style.display = "none";
          }
          if (this.rate) {
            this.updateRate();
          } else {
            this.createRate();
          }
        } else {
          this.$el.querySelector(".is-invalid").scrollIntoView(false);
        }
      });
    },

    createRate() {
      this.saving = true;

      axios
        .post("/carry-freight/fixed-rates", this.mapForm())
        .then(response => {
          this.saving = false;

          this.$swal({
            title: "Fixed rate offer created!",
            type: "success",
            confirmButtonText: "Okay"
          });

          this.goRates();
          this.$emit("rateChanged");
        })
        .catch(response => {
          this.saving = false;
          Event.$emit("serverError");
          this.$emit("cancelled");
        });
    },

    updateRate() {
      this.saving = true;

      axios
        .patch("/carry-freight/fixed-rates/" + this.rate.id, this.mapForm())
        .then(response => {
          this.saving = false;

          this.$swal({
            title: "Fixed rate offer updated!",
            type: "success",
            confirmButtonText: "Okay"
          });

          this.goRates();
          this.$emit("rateChanged");
        })
        .catch(response => {
          this.saving = false;
          Event.$emit("serverError");
          this.$emit("cancelled");
        });
    },

    fetchFixedRate() {
      axios
        .get("/carry-freight/fixed-rates/id/" + this.$route.params.id)
        .then(response => {
          this.rate = response.data.data[0];
          this.setRateDefaults();
        })
        .catch(errors => {
          this.errorMsg =
            "Sorry, you don't have the access for this fixed rate.";
        });
    },

    mapForm() {
      let goodsTypes = [];

      for (var index in this.form.goodsTypes) {
        goodsTypes.push(this.form.goodsTypes[index].id);
      }

      //
      let packageTypes = [];

      for (var index in this.form.packageTypes) {
        packageTypes.push(this.form.packageTypes[index].id);
      }

      //
      let pickupDays = [];

      for (var index in this.form.pickupDays) {
        pickupDays.push(this.form.pickupDays[index].id);
      }

      //
      let deliveryDays = [];

      for (var index in this.form.deliveryDays) {
        deliveryDays.push(this.form.deliveryDays[index].id);
      }

      return {
        pickup_area: this.form.pickupArea,
        delivery_area: this.form.deliveryArea,
        goods_types: goodsTypes,
        package_types: packageTypes,
        rate: this.form.rate,
        rate_type: this.form.rateType,
        min_charge: this.form.minCharge,
        flag_fall: this.form.flagFall,
        max_capacity: this.form.maxDailyCapacity,
        cutoff_time: this.form.cutoffTime,
        cutoff_day: this.form.cutoffDay,
        service_level: this.form.serviceLevel,
        live: this.form.live,
        offer_expiry: moment(this.form.expiry).format("DD-MM-YYYY"),
        pickup_days: pickupDays,
        delivery_days: deliveryDays,
        start_restriction: this.form.startMonthRestriction,
        end_restriction: this.form.endMonthRestriction,
        volume_to_weight: this.form.volumeWeightRatio
      };
    },

    setRateDefaults() {
      let goodsTypes = [];

      for (var index in this.rate.goodTypes.data) {
        goodsTypes.push({
          id: this.rate.goodTypes.data[index].id,
          name: this.rate.goodTypes.data[index].name
        });
      }

      //
      let packageTypes = [];

      for (var index in this.rate.packageTypes.data) {
        packageTypes.push({
          id: this.rate.packageTypes.data[index].id,
          name: this.rate.packageTypes.data[index].name
        });
      }

      this.form = {
        pickupArea: this.rate.pickupArea.data.id,
        deliveryArea: this.rate.deliveryArea.data.id,
        goodsTypes: goodsTypes,
        packageTypes: packageTypes,
        rate: this.rate.rate,
        rateType: this.rate.rate_calculation_type,
        volumeWeightRatio: this.rate.volume_to_weight,
        minCharge: this.rate.min_charge,
        flagFall: this.rate.flag_fall,
        maxDailyCapacity: this.rate.max_daily_capacity,
        cutoffTime: this.rate.cutoff_time,
        cutoffDay: this.rate.cutoff_day,
        serviceLevel: this.rate.service_level,
        pickupDays: this.rate.pickup_days,
        deliveryDays: this.rate.delivery_days,
        live: this.rate.live ? "1" : "0",
        expiry: moment(this.rate.expiry, "YYYY-MM-DD").toDate(),
        startMonthRestriction: this.rate.start_restriction,
        endMonthRestriction: this.rate.end_restriction
      };
    },

    loadPackageTypes() {
      axios
        .get("/api/package-types")
        .then(response => {
          this.packageTypes = response.data.data;

          if (!this.rate) {
            this.form.packageTypes.push(this.packageTypes[0]);
          }
        })
        .catch(error => {
          Event.$emit("serverError");
        });
    },

    loadGoodTypes() {
      axios
        .get("/api/goods-types")
        .then(response => {
          this.goodTypes = response.data.data;

          if (!this.rate) {
            this.form.goodsTypes.push(this.goodTypes[0]);
          }
        })
        .catch(error => {
          Event.$emit("serverError");
        });
    },

    loadCustomAreas() {
      axios.get("/settings/custom-areas").then(response => {
        this.customAreas = response.data.data;
        if (!this.rate && this.customAreas.length > 0) {
          this.form.pickupArea = this.customAreas[0].id;
          this.form.deliveryArea = this.customAreas[0].id;
        }
      });
    },

    moment: function(...args) {
      return moment(...args);
    }
  },

  computed: {
    loading() {
      return (
        !this.packageTypes ||
        !this.goodTypes ||
        !this.customAreas ||
        (!this.isCreate && !this.rate)
      );
    },

    hasError() {
      return this.errorMsg;
    },

    modalTitle() {
      if (this.rate) {
        return (
          "Updating offer: " +
          this.rate.pickupArea.data.name +
          " to " +
          this.rate.deliveryArea.data.name
        );
      }

      if (this.isCreate) {
        return "Create new offer";
      }

      return "";
    },

    submitButtonText() {
      if (this.rate) {
        return "Update";
      }

      return "Create";
    },

    days() {
      return [
        {
          id: 1,
          name: "Monday"
        },
        {
          id: 2,
          name: "Tuesday"
        },
        {
          id: 3,
          name: "Wednesday"
        },
        {
          id: 4,
          name: "Thursday"
        },
        {
          id: 5,
          name: "Friday"
        },
        {
          id: 6,
          name: "Saturday"
        },
        {
          id: 7,
          name: "Sunday"
        }
      ];
    }
  },

  watch: {
    "form.goodsTypes": function() {
      if (this.form.goodsTypes.length === 0) {
        this.errors.add({
          field: "goodsTypes",
          msg: "Please select at least one type of good"
        });
      } else {
        this.errors.remove("goodsTypes");
      }
    },
    "form.packageTypes": function() {
      if (this.form.packageTypes.length === 0) {
        this.errors.add({
          field: "packageTypes",
          msg: "Please select at least one type of package"
        });
      } else {
        this.errors.remove("packageTypes");
      }
    },
    "form.pickupDays": function() {
      if (this.form.pickupDays.length === 0) {
        this.errors.add({
          field: "pickupDays",
          msg: "Please select at least one pickup day"
        });
      } else {
        this.errors.remove("pickupDays");
      }
    },
    "form.deliveryDays": function() {
      if (this.form.deliveryDays.length === 0) {
        this.errors.add({
          field: "deliveryDays",
          msg: "Please select at least one delivery day"
        });
      } else {
        this.errors.remove("deliveryDays");
      }
    },
    "form.rate": function() {
      if (this.fields.rate) {
        this.$validator.validate("rate");
      }
    },
    "form.minCharge": function() {
      if (this.fields.minCharge) {
        this.$validator.validate("minCharge");
      }
    },
    "form.maxDailyCapacity": function() {
      if (this.fields.maxDailyCapacity) {
        this.$validator.validate("maxDailyCapacity");
      }
    },
    "form.flagFall": function() {
      if (this.fields.flagFall) {
        this.$validator.validate("flagFall");
      }
    }
  }
};
</script>
